import { useState } from 'react'
import { Check, Undo2, Trash2, Send, AlertTriangle } from 'lucide-react'
import type { Comment } from '../../hooks/useComments'

interface Props {
  comment: Comment
  isDetached?: boolean
  isActive?: boolean
  onReply: (commentId: string, body: string) => Promise<Comment | null>
  onResolve: (commentId: string) => void
  onUnresolve: (commentId: string) => void
  onDelete: (commentId: string) => void
  onClick?: () => void
}

function formatTime(dateStr: string) {
  const date = new Date(dateStr)
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return date.toLocaleDateString()
}

export default function CommentThread({
  comment,
  isDetached = false,
  isActive = false,
  onReply,
  onResolve,
  onUnresolve,
  onDelete,
  onClick,
}: Props) {
  const [replyBody, setReplyBody] = useState('')
  const [showReply, setShowReply] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const handleReply = async () => {
    if (!replyBody.trim()) return
    setSubmitting(true)
    await onReply(comment.id, replyBody.trim())
    setReplyBody('')
    setShowReply(false)
    setSubmitting(false)
  }

  return (
    <div
      id={`comment-thread-${comment.id}`}
      className={`rounded-lg border p-3 transition-colors ${
        isActive ? 'border-amber-400 bg-amber-50/50' : 'border-gray-200 bg-white'
      } ${comment.resolved ? 'opacity-60' : ''}`}
      onClick={onClick}
    >
      {/* Anchor text blockquote */}
      <div className="mb-2 rounded bg-gray-50 px-2.5 py-1.5 text-xs text-gray-500 border-l-2 border-amber-400 italic">
        "{comment.anchor_text}"
        {isDetached && (
          <span className="ml-1.5 inline-flex items-center gap-0.5 rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-medium text-orange-700 not-italic">
            <AlertTriangle className="h-2.5 w-2.5" /> detached
          </span>
        )}
      </div>

      {/* Root comment */}
      <div className="mb-1">
        <div className="flex items-center gap-1.5 text-xs">
          <span className="font-medium text-gray-900">{comment.author}</span>
          <span className="text-gray-400">{formatTime(comment.created_at)}</span>
        </div>
        <p className="mt-0.5 text-sm text-gray-700">{comment.body}</p>
      </div>

      {/* Replies */}
      {comment.replies.length > 0 && (
        <div className="mt-2 space-y-2 border-l-2 border-gray-100 pl-3">
          {comment.replies.map(reply => (
            <div key={reply.id}>
              <div className="flex items-center gap-1.5 text-xs">
                <span className="font-medium text-gray-900">{reply.author}</span>
                <span className="text-gray-400">{formatTime(reply.created_at)}</span>
              </div>
              <p className="mt-0.5 text-sm text-gray-700">{reply.body}</p>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="mt-2 flex items-center gap-1">
        {!comment.resolved ? (
          <button
            onClick={(e) => { e.stopPropagation(); onResolve(comment.id) }}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-500 hover:bg-green-50 hover:text-green-700 transition-colors"
            title="Resolve"
          >
            <Check className="h-3 w-3" /> Resolve
          </button>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); onUnresolve(comment.id) }}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-500 hover:bg-amber-50 hover:text-amber-700 transition-colors"
            title="Unresolve"
          >
            <Undo2 className="h-3 w-3" /> Reopen
          </button>
        )}

        <button
          onClick={(e) => { e.stopPropagation(); setShowReply(!showReply) }}
          className="flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
        >
          Reply
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation()
            if (confirm('Delete this comment thread?')) onDelete(comment.id)
          }}
          className="ml-auto flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
          title="Delete"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>

      {/* Reply input */}
      {showReply && (
        <div className="mt-2 flex items-center gap-1.5">
          <input
            type="text"
            value={replyBody}
            onChange={(e) => setReplyBody(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); handleReply() }
              if (e.key === 'Escape') { setShowReply(false); setReplyBody('') }
            }}
            placeholder="Reply..."
            className="flex-1 rounded border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs outline-none focus:border-gray-400 placeholder:text-gray-400"
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={(e) => { e.stopPropagation(); handleReply() }}
            disabled={!replyBody.trim() || submitting}
            className="rounded bg-gray-900 p-1.5 text-white hover:bg-gray-800 disabled:opacity-40 transition-colors"
          >
            <Send className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  )
}
